import React, { useState } from 'react';
import { getUserStats, getRecoveryHistory } from '../services/storage';
import { ACHIEVEMENTS, getUnlockedAchievements, getAchievementProgress, RARITY_COLORS } from '../data/achievements';
import ActivityCalendar from './ActivityCalendar';

const UserStatsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('stats'); // 'stats' или 'achievements'
  
  if (!isOpen) return null;

  const stats = getUserStats();
  const history = getRecoveryHistory();
  
  // Расширенная статистика для достижений
  const extendedStats = {
    ...stats,
    historySize: history.length,
    tagsUsed: history.filter(item => item.tags && item.tags.length > 0).length,
    shares: parseInt(localStorage.getItem('total_shares') || '0'),
    featuresUsed: parseInt(localStorage.getItem('features_used') || '0'),
    vpnClientsExplored: parseInt(localStorage.getItem('vpn_clients_explored') || '0'),
    userRating: parseInt(localStorage.getItem('user_rating') || '0'),
    allOrganized: history.length > 0 && history.every(item => item.note && item.tags && item.tags.length > 0)
  };
  
  const unlockedAchievements = getUnlockedAchievements(extendedStats);
  const totalAchievements = ACHIEVEMENTS.length;
  
  // Получаем последние 7 дней для графика
  const last7Days = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    last7Days.push({
      date: dateStr,
      count: stats.byDate?.[dateStr] || 0,
      label: date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
    });
  }

  const maxCount = Math.max(...last7Days.map(d => d.count), 1);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Заголовок */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <svg className="w-7 h-7 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Ваша статистика
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Табы */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'stats'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📊 Статистика
            </button>
            <button
              onClick={() => setActiveTab('achievements')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                activeTab === 'achievements'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🏆 Достижения ({unlockedAchievements.length}/{totalAchievements})
            </button>
          </div>

          {activeTab === 'stats' && (
            <>
          {/* Основные метрики */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Всего восстановлено</p>
                  <p className="text-3xl font-bold mt-1">{stats.totalRestored}</p>
                </div>
                <svg className="w-12 h-12 opacity-30" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Текущая серия</p>
                  <p className="text-3xl font-bold mt-1">{stats.currentStreak}</p>
                  <p className="text-xs opacity-75 mt-1">дней подряд</p>
                </div>
                <svg className="w-12 h-12 opacity-30" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Рекорд серии</p>
                  <p className="text-3xl font-bold mt-1">{stats.maxStreak}</p>
                  <p className="text-xs opacity-75 mt-1">дней подряд</p>
                </div>
                <svg className="w-12 h-12 opacity-30" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
            </div>
          </div>

          {/* График активности за последние 7 дней */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
              Активность за последние 7 дней
            </h3>
            <div className="flex items-end justify-between gap-2 h-32">
              {last7Days.map((day, index) => {
                const height = (day.count / maxCount) * 100;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div className="relative w-full flex items-end justify-center" style={{ height: '100px' }}>
                      <div 
                        className="w-full bg-gradient-to-t from-primary-600 to-primary-400 rounded-t-md transition-all duration-300 hover:from-primary-700 hover:to-primary-500"
                        style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                        title={`${day.count} заказов`}
                      >
                        {day.count > 0 && (
                          <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-gray-700">
                            {day.count}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 mt-2">{day.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Календарь активности */}
          <ActivityCalendar />

          {/* Достижения - компактная версия */}
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-4 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Последние достижения
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className={`text-center p-3 rounded-lg ${stats.totalRestored >= 1 ? 'bg-white shadow-sm' : 'bg-gray-100 opacity-50'}`}>
                <div className="text-2xl mb-1">🎯</div>
                <div className="text-xs font-medium text-gray-700">Первый шаг</div>
                <div className="text-xs text-gray-500">1 восстановление</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${stats.totalRestored >= 5 ? 'bg-white shadow-sm' : 'bg-gray-100 opacity-50'}`}>
                <div className="text-2xl mb-1">⚡</div>
                <div className="text-xs font-medium text-gray-700">Активный</div>
                <div className="text-xs text-gray-500">5 восстановлений</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${stats.totalRestored >= 10 ? 'bg-white shadow-sm' : 'bg-gray-100 opacity-50'}`}>
                <div className="text-2xl mb-1">🔥</div>
                <div className="text-xs font-medium text-gray-700">Мастер</div>
                <div className="text-xs text-gray-500">10 восстановлений</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${stats.maxStreak >= 3 ? 'bg-white shadow-sm' : 'bg-gray-100 opacity-50'}`}>
                <div className="text-2xl mb-1">🏆</div>
                <div className="text-xs font-medium text-gray-700">Постоянный</div>
                <div className="text-xs text-gray-500">3 дня подряд</div>
              </div>
            </div>
          </div>

            </>
          )}

          {activeTab === 'achievements' && (
            <div className="space-y-4">
              {/* Прогресс разблокировки */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Прогресс разблокировки</span>
                  <span className="text-sm font-bold text-primary-600">
                    {Math.round((unlockedAchievements.length / totalAchievements) * 100)}%
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 transition-all duration-500"
                    style={{ width: `${(unlockedAchievements.length / totalAchievements) * 100}%` }}
                  />
                </div>
              </div>

              {/* Список достижений */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto pr-2">
                {ACHIEVEMENTS.map(achievement => {
                  const isUnlocked = unlockedAchievements.some(a => a.id === achievement.id);
                  const progress = getAchievementProgress(achievement.id, extendedStats);
                  const rarity = RARITY_COLORS[achievement.rarity];

                  return (
                    <div
                      key={achievement.id}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isUnlocked
                          ? 'bg-white shadow-md'
                          : 'bg-gray-50 opacity-60'
                      }`}
                      style={{
                        borderColor: isUnlocked ? rarity.bg : '#E5E7EB'
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`text-4xl ${!isUnlocked && 'grayscale opacity-50'}`}>
                          {achievement.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-bold text-sm text-gray-900 truncate">
                              {achievement.title}
                            </h4>
                            {isUnlocked && (
                              <span 
                                className="px-2 py-0.5 text-xs font-medium text-white rounded-full"
                                style={{ backgroundColor: rarity.bg }}
                              >
                                {rarity.text}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mb-2">
                            {achievement.description}
                          </p>
                          
                          {!isUnlocked && progress > 0 && (
                            <div className="mb-2">
                              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary-500 transition-all"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 mt-1">
                                {Math.round(progress)}%
                              </span>
                            </div>
                          )}
                          
                          {isUnlocked && (
                            <div className="flex items-center text-xs text-green-600 font-medium">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Разблокировано!
                            </div>
                          )}
                          
                          {achievement.reward && (
                            <p className="text-xs text-purple-600 mt-1 italic">
                              🎁 {achievement.reward}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Кнопка закрытия */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStatsModal;

